import { useState, useEffect } from 'react';
import {
    Search, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Copy, CircleEllipsis, Pencil, Plus, Key, Trash2,
    AlertTriangle, X, Radio, Network, Globe, Layers, Server,
    Cctv, Monitor, HardDrive, Printer, Shield, Box,
    Check, Cpu, Factory, Hash,
    Loader2,
    Columns3,
    Tag,
    ArrowUp, ArrowDown, ArrowUpDown,
    Info, Calendar,
    ShieldCheck,
    Eye,
    Smartphone,
    Clock,
    User,
    Import,
    Play,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { jwt_decode } from '../../utils/jwt_decode';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClientLevel } from '../../hooks/useClientLevel';
import { hasPermission } from '../../utils/permissionsHelper';
import AgentExportModal from '../Agent/AgentExportModal';
import AddAssetModal from './AddAssetModal';
import AddCredentialModal from './AddCredentialModal';
import AddTagModal from './AddTagModal';
import UploadModal from './UploadModal';
import LocationSelectorSection, { type LocationIds } from '../../components/LocationSelectorSection';
import LiveStreamModal, { type CameraType } from './LiveStreamModal';

// --- Interfaces ---
interface Asset {
    id: number;
    agent_id: number;
    client_id: number;
    organization_id: number;
    name: string;
    asset_type: string;
    hostname: string;
    ip_address: string;
    status: string;
    os_name?: string;
    manufacturer?: string;
    mac_address?: string;
    location: string;
    switch_role: string | null;
    total_ports: number | null;
    is_primary: boolean;
    tags: string;
    resolved_tags?: string | any[];
    protocols_performed?: string | string[];
    purchase_date?: string | null;
    installation_date?: string | null;
    warranty_expiration?: string | null;
    warranty_details?: string | null;
    amc_expiration?: string | null;
    amc_provider?: string | null;
    amc_details?: string | null;
    end_of_life?: string | null;
    end_of_support?: string | null;
    is_ad_machine?: boolean;
    last_seen?: string | null;
    client_name?: string | null;
    has_oob?: boolean;
    oob_type?: string;
}

interface DeleteConfirmationModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    assetName: string | undefined;
    isDeleting: boolean;
}

// --- Constants & Config ---
const ASSET_TYPES = [
    { id: 'all', label: 'All Assets', icon: Layers },
    { id: 'server', label: 'EndPoint Servers', icon: Server },
    { id: 'switch', label: 'Switches', icon: Network },
    { id: 'camera', label: 'Cameras', icon: Cctv },
    { id: 'unmanaged_switch', label: 'Unmanaged Switchs', icon: Network },
    { id: 'workstation', label: 'Workstations', icon: Monitor },
    { id: 'access_point', label: 'Access Points', icon: Radio },
    { id: 'nvr', label: 'NVR', icon: HardDrive },
    { id: 'printer', label: 'Printers', icon: Printer },
    { id: 'firewall', label: 'Firewalls', icon: Shield },
    { id: 'router', label: 'Routers', icon: Network },
    { id: 'storage', label: 'Storage Devices', icon: HardDrive },
    { id: 'mobile', label: 'Mobile', icon: Smartphone },
    { id: 'ilo', label: 'iLO', icon: Server },
    { id: 'other', label: 'Other', icon: Box }
];

const getAssetIconConfig = (type: string) => {
    const t = type?.toLowerCase() || '';
    const match = ASSET_TYPES.find(a => a.id !== 'all' && t.includes(a.id));
    const config = match || ASSET_TYPES.find(a => a.id === 'other')!;
    const colors: Record<string, string> = {
        camera: 'text-emerald-500',
        server: 'text-blue-500',
        switch: 'text-cyan-500',
        router: 'text-[#422568]',
        all: 'text-[#422568]',
        mobile: 'text-green-500'
    };
    return { ...config, color: colors[config.id] || 'text-slate-400' };
};

export const getOSIcon = (type?: string) => {
    if (!type) return { icon: "/device.svg" };
    const os = type.toLowerCase();
    if (os.includes("cisco")) return { icon: "/cisco.svg" };
    if (os.includes("mac") || os.includes("darwin") || os === "ios" || os.includes("osx")) return { icon: "/mac.png" };
    if (os.includes("windows")) return { icon: "/windows.png" };
    if (os.includes("ubuntu")) return { icon: "/ubuntu.png" };
    if (os.includes("linux") || os.includes("debian") || os.includes("centos") || os.includes("redhat") || os.includes("fedora")) return { icon: "/linux.svg" };
    if (os.includes("android")) return { icon: "/android.svg" };
    return { icon: "/device.svg" };
};

// --- Sub-components ---
const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({ open, onClose, onConfirm, assetName, isDeleting }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[9999] flex items-center justify-center p-4">
            <div className="bg-white border border-slate-200 rounded-xl shadow-2xl max-w-xs w-full overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-4">
                    <div className="flex justify-between items-start mb-3">
                        <div className="p-2 bg-rose-50 rounded-lg"><AlertTriangle className="w-5 h-5 text-rose-500" /></div>
                        <button onClick={onClose} disabled={isDeleting} className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"><X className="w-4 h-4" /></button>
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 mb-1">Delete Asset?</h3>
                    <p className="text-slate-500 text-[11px] mb-4 leading-relaxed">Are you sure you want to delete <span className="text-slate-900 font-bold">"{assetName}"</span>? This action cannot be undone.</p>
                    <div className="flex gap-2">
                        <button onClick={onClose} disabled={isDeleting} className="flex-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[11px] font-bold transition-all disabled:opacity-50 uppercase tracking-wider">Cancel</button>
                        <button onClick={onConfirm} disabled={isDeleting} className="flex-1 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-[11px] font-bold transition-all flex items-center justify-center disabled:opacity-50 uppercase tracking-wider">{isDeleting ? 'Deleting...' : 'Delete'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main Component ---
const AssetsTable = ({ clientId }: { clientId?: number }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const rawType = searchParams.get('asset_type') || 'all';
    const agentId = searchParams.get('agent_id');
    const typeFilter = rawType === 'local_asset' ? 'server' : rawType;
    const statusFilter = (searchParams.get('status') as 'all' | 'active' | 'offline') || 'all';

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [assetsData, setAssetsData] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const currentPage = parseInt(searchParams.get('page') || '1', 10) || 1;
    const itemsPerPage = parseInt(searchParams.get('limit') || '30', 10) || 30;
    const sortBy = searchParams.get('sort_by') || 'last_seen';
    const sortOrder = (searchParams.get('sort_order') as 'asc' | 'desc') || 'desc';
    const [showExportModal, setShowExportModal] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    // const [total, setTotal] = useState(0);

    const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
        asset_name: true,
        os: false,
        manufacturer: false,
        mac: false,
        ip_address: false,
        hostname: false,
        type: true,
        status: true,
        tags: true,
        protocols: true,
        last_frame: true,
        live_stream: true,
        last_seen: true,
        client_name: true,
    });
    const [colMenuOpen, setColMenuOpen] = useState(false);
    const [liveStreamModalOpen, setLiveStreamModalOpen] = useState(false);
    const [liveStreamCamera, setLiveStreamCamera] = useState<CameraType | null>(null);

    const [assetModalOpen, setAssetModalOpen] = useState(false);
    const [assetModalMode, setAssetModalMode] = useState<'add' | 'edit'>('add');
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
    const [importModal, setImportModal] = useState(false);
    const [credentialModalOpen, setCredentialModalOpen] = useState(false);
    const [credentialAssetId, setCredentialAssetId] = useState<number | null>(null);
    const [credentialClientId, setCredentialClientId] = useState<any>(null);
    const [initialAgentId, setInitialAgentId] = useState<number | null>(null);
    const [isCamera, setIsCamera] = useState(false);
    const [credentialAssetType, setCredentialAssetType] = useState<string>('');
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [assetToDelete, setAssetToDelete] = useState<Asset | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [locationIds, setLocationIds] = useState<LocationIds>({
        regionId: 2,
        stateId: 1,
        districtId: null,
        townId: null,
    });
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [tagModalOpen, setTagModalOpen] = useState(false);
    const [tagAssetId, setTagAssetId] = useState<number | null>(null);
    const [tagAssetName, setTagAssetName] = useState<string>('');
    const [infoPopoverAsset, setInfoPopoverAsset] = useState<Asset | null>(null);
    const [lastFrameModalOpen, setLastFrameModalOpen] = useState(false);
    const [lastFrameImage, setLastFrameImage] = useState<string | null>(null);
    const [lastFrameLoading, setLastFrameLoading] = useState(false);
    const [lastFrameAssetName, setLastFrameAssetName] = useState<string>('');
    // const currentPath = useLocation();
    const canCreate = hasPermission('assets', 'create');
    // const canDelete = hasPermission('assets', 'delete');
    // const canEdit = hasPermission('assets', 'update');

    const navigate = useNavigate();
    // const [expandedProtocols, setExpandedProtocols] = useState<Record<string, boolean>>({});
    const level = useClientLevel();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    useEffect(() => {
        const rid = searchParams.get('region_id');
        const sid = searchParams.get('state_id');
        const did = searchParams.get('district_id');
        const tid = searchParams.get('town_id');

        setLocationIds(prev => {
            const next = {
                regionId: rid ? (isNaN(Number(rid)) ? rid : Number(rid)) : 2,
                stateId: sid ? (isNaN(Number(sid)) ? sid : Number(sid)) : 1,
                districtId: did ? (isNaN(Number(did)) ? did : Number(did)) : null,
                townId: tid ? (isNaN(Number(tid)) ? tid : Number(tid)) : null,
            };
            if (JSON.stringify(prev) !== JSON.stringify(next)) return next;
            return prev;
        });
    }, [searchParams]);

    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setSearchParams(prev => {
                    const next = new URLSearchParams(prev);
                    next.set('page', '1');
                    return next;
                }, { replace: true });
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (openMenuId && !target.closest('[data-menu-id]')) setOpenMenuId(null);
            if (colMenuOpen && !target.closest('[data-col-menu]')) setColMenuOpen(false);
            if (infoPopoverAsset && !target.closest('[data-info-popover]')) setInfoPopoverAsset(null);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [openMenuId, colMenuOpen, infoPopoverAsset]);

    const fetchData = async (skip: number = 0, limit: number = itemsPerPage) => {
        try {
            setLoading(true);
            const queryObj: any = level?.id ? { client_id: level.id } : {};
            const apiAssetType = typeFilter === "server" ? "local_asset" : typeFilter === "other" ? "unknown" : typeFilter;

            if (apiAssetType !== 'all') queryObj.asset_type = apiAssetType;
            else queryObj.asset_type = { $notin: ['isp'] };

            if (statusFilter !== 'all') queryObj.status = statusFilter;
            if (agentId) queryObj.agent_id = Number(agentId);
            if (clientId) queryObj.client_id = Number(clientId);

            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { "$ilike": `%${debouncedSearch}%` } },
                    { ip_address: { "$ilike": `%${debouncedSearch}%` } },
                    { hostname: { "$ilike": `%${debouncedSearch}%` } },
                    { mac_address: { "$ilike": `%${debouncedSearch}%` } }
                ];
            }

            if (locationIds.regionId) queryObj.region_id = locationIds.regionId;
            if (locationIds.stateId) queryObj.state_id = locationIds.stateId;
            if (locationIds.districtId) queryObj.district_id = locationIds.districtId;
            if (locationIds.townId) queryObj.town_id = locationIds.townId;

            const params: any = {
                query: JSON.stringify(queryObj),
                skip, limit,
                sort_by: sortBy, sort_order: sortOrder,
            };

            const response = await baseapi.get('/assets', { params });
            if (response.data.status) {
                setAssetsData(response.data.data);
                setTotalPages(Math.ceil(response.data.total / limit));
                setTotalItems(response.data.total);
                // setTotal(response.data.total);
            } else {
                setAssetsData([]); setTotalPages(0); setTotalItems(0);
            }
        } catch (error: any) { showError(error.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
    }, [currentPage, itemsPerPage, typeFilter, statusFilter, debouncedSearch, level, sortBy, sortOrder, locationIds]);

    const handleRefresh = () => fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);

    const handleLocationChange = (newIds: LocationIds) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (newIds.regionId && newIds.regionId !== 2) next.set('region_id', String(newIds.regionId)); else next.delete('region_id');
            if (newIds.stateId && newIds.stateId !== 1) next.set('state_id', String(newIds.stateId)); else next.delete('state_id');
            if (newIds.districtId) next.set('district_id', String(newIds.districtId)); else next.delete('district_id');
            if (newIds.townId) next.set('town_id', String(newIds.townId)); else next.delete('town_id');
            next.set('page', '1');
            return next;
        }, { replace: true });
    };

    const copyToClipboard = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) { console.error('Failed to copy: ', err); }
    };

    // const getStatusColor = (status: string) => {
    //     const s = status?.toLowerCase();
    //     if (s === 'active') return 'text-emerald-600';
    //     if (s === 'offline') return 'text-rose-600';
    //     return 'text-slate-500';
    // };

    const getStatusBg = (status: string) => {
        const s = status?.toLowerCase();
        if (s === 'active') return 'bg-emerald-500';
        if (s === 'offline') return 'bg-rose-500';
        return 'bg-slate-400';
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('page', String(page));
                return next;
            }, { replace: true });
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('limit', String(value));
            next.set('page', '1');
            return next;
        }, { replace: true });
    };

    const handleSort = (field: string) => {
        let nextOrder: 'asc' | 'desc' = 'asc';
        if (sortBy === field) {
            nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        }
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('sort_by', field);
            next.set('sort_order', nextOrder);
            return next;
        }, { replace: true });
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortBy !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-slate-400" />;
        return sortOrder === 'asc'
            ? <ArrowUp className="w-2.5 h-2.5 ml-1 text-[#422568]" />
            : <ArrowDown className="w-2.5 h-2.5 ml-1 text-[#422568]" />;
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({ top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX - 120 });
        setOpenMenuId(prev => (prev === String(id) ? null : String(id)));
    };

    const handleInfoClick = (e: React.MouseEvent, asset: Asset) => {
        e.stopPropagation();
        setInfoPopoverAsset(asset);
    };

    const handleEdit = (asset: Asset) => {
        setSelectedAsset(asset); setAssetModalMode('edit'); setAssetModalOpen(true);
        setInitialAgentId(asset.agent_id); setIsCamera(asset.asset_type === 'camera');
        setOpenMenuId(null);
    };

    const handleAssetClick = (has_oob: boolean = false, oob_type: string = "", is_ad_machine: boolean = false, name: string, assetId: any, client_id: any, agent_id: any, organization_id: any, assettype: any, hostname: string, path: string = `${window.location.pathname}${window.location.search}`) => {
        localStorage.setItem('internal_ids', JSON.stringify({ 'is_ad_machine': is_ad_machine, 'asset_id': assetId, 'client_id': client_id, 'agent_id': agent_id, 'organization_id': organization_id, asset_type: assettype, host_name: hostname, path: path, name: name, has_oob: has_oob, oob_type: oob_type }));
        navigate("/asset/dashboard");
    };

    const handleCredentials = (asset: Asset) => {
        setCredentialAssetId(asset.id); setCredentialClientId(asset.client_id);
        setCredentialAssetType(asset.asset_type || '');
        setCredentialModalOpen(true); setIsCamera(asset.asset_type === 'camera');
        setOpenMenuId(null);
    };

    const handleAddTags = (asset: Asset) => {
        setTagAssetId(asset.id);
        setTagAssetName(asset.name);
        setTagModalOpen(true);
        setOpenMenuId(null);
    };

    const fetchLastFrame = async (clientId: number, assetId: number, assetName: string) => {
        try {
            setLastFrameLoading(true);
            setLastFrameModalOpen(true);
            setLastFrameImage(null);
            setLastFrameAssetName(assetName);
            const res = await baseapi.get('/camera_frames', {
                params: { query: JSON.stringify({ client_id: clientId, asset_id: assetId }) }
            });
            if (res.data.status && res.data.data && res.data.data.length > 0) {
                setLastFrameImage(res.data.data[0].last_frame);
            } else {
                setLastFrameImage(null);
            }
        } catch (error: any) {
            showError(error.message || 'Failed to fetch camera frame');
            setLastFrameImage(null);
        } finally {
            setLastFrameLoading(false);
        }
    };

    const handleOpenLiveStream = (item: Asset) => {
        try {
            const jwt = jwt_decode();
            const tenant_name = jwt.tenant_id || jwt.tenant_name;
            const org_id = jwt.organization_id;

            const streamPath = tenant_name
                ? `${tenant_name}-${item.organization_id || org_id}-${item.client_id}-${item.agent_id}_${item.id}`
                : `${item.organization_id || org_id}-${item.client_id}-${item.agent_id}_${item.id}`;

            const cameraObj: CameraType = {
                id: String(item.id),
                name: item.name,
                status: item.status?.toLowerCase() === 'active' || item.status?.toLowerCase() === 'online' ? 'online' : 'offline',
                url: `https://${window.location.hostname}/${streamPath}/`,
                client_id: String(item.client_id),
                agent_id: String(item.agent_id)
            };

            setLiveStreamCamera(cameraObj);
            setLiveStreamModalOpen(true);
        } catch (error: any) {
            showError("Failed to decode token or generate stream URL");
            console.error(error);
        }
    };

    const handleDeleteClick = (asset: Asset) => { setAssetToDelete(asset); setDeleteModalOpen(true); setOpenMenuId(null); };

    const confirmDelete = async () => {
        if (!assetToDelete) return;
        try {
            setIsDeleting(true);
            const res = await baseapi.delete(`/assets/${assetToDelete.id}`);
            if (res.data.status) { showSuccess('Asset deleted successfully'); handleRefresh(); }
            else showError(res.data.message || 'Failed to delete asset');
        } catch (error: any) { showError(error.message); }
        finally { setIsDeleting(false); setDeleteModalOpen(false); setAssetToDelete(null); }
    };

    const formatDate = (ts: string | null | undefined) => {
        if (!ts) return "-";
        const fixed = ts.slice(0, 23) + "Z";
        return new Date(fixed).toLocaleString();
    };

    return (
        <div className="bg-slate-50 p-3">
            <div className="w-full mx-auto">
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-1">
                    <div className="flex items-center gap-3">
                        <div className="bg-[#422568] p-2 rounded-lg shadow-lg shadow-[#422568]/20">
                            <Box className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="font-black text-xl text-slate-900 tracking-tight">Assets</h1>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none mt-1">Surveillance Devices</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">

                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search Name, IP, MAC..."
                                className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-1.5 text-[11px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all font-medium placeholder:text-slate-400 shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button onClick={handleRefresh} disabled={loading} className="p-2 bg-white border border-slate-200 rounded-lg text-slate-500 hover:text-[#422568] hover:bg-slate-50 transition-all shadow-sm">
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>

                        <div className="relative" data-col-menu>
                            <button onClick={() => setColMenuOpen(!colMenuOpen)} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all shadow-sm">
                                <Columns3 className="w-3.5 h-3.5 text-slate-500" />
                                <span className="text-[10px] font-black uppercase text-slate-600 tracking-wider">Columns</span>
                            </button>
                            {colMenuOpen && (
                                <div className="absolute top-full mt-2 right-0 w-56 bg-white border border-slate-200 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] z-[110] p-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
                                    <div className="px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 mb-1">Display Columns</div>
                                    <div className="max-h-[400px] overflow-y-auto custom-scroll">
                                        {[
                                            { id: 'asset_name', label: 'Asset Name', icon: Server },
                                            { id: 'os', label: 'Operating System', icon: Cpu },
                                            { id: 'manufacturer', label: 'Manufacturer', icon: Factory },
                                            { id: 'mac', label: 'MAC Address', icon: Hash },
                                            { id: 'ip_address', label: 'IP Address', icon: Network },
                                            { id: 'hostname', label: 'Hostname', icon: Globe },
                                            { id: 'type', label: 'Asset Type', icon: Layers },
                                            { id: 'status', label: 'Status', icon: ShieldCheck },
                                            { id: 'tags', label: 'Tags', icon: Tag },
                                            { id: 'last_seen', label: 'Last Seen', icon: Clock },
                                            { id: 'client_name', label: 'Client Name', icon: User },
                                            ...(typeFilter === 'camera' ? [
                                                { id: 'last_frame', label: 'Last Frame', icon: Eye },
                                                { id: 'live_stream', label: 'Live Stream', icon: Play }
                                            ] : []),
                                        ].map(col => (
                                            <button key={col.id} onClick={() => setVisibleColumns(prev => ({ ...prev, [col.id]: !prev[col.id] }))} className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold text-slate-600 hover:bg-slate-50 rounded-lg transition-all">
                                                <div className="flex items-center gap-2.5">
                                                    <col.icon className={`w-3.5 h-3.5 ${visibleColumns[col.id] ? 'text-[#422568]' : 'text-slate-300'}`} />
                                                    <span className={visibleColumns[col.id] ? 'text-slate-900' : 'text-slate-400'}>{col.label}</span>
                                                </div>
                                                <div className={`w-3.5 h-3.5 rounded-md border-2 flex items-center justify-center transition-all ${visibleColumns[col.id] ? 'bg-[#422568] border-[#422568]' : 'border-slate-200'}`}>
                                                    {visibleColumns[col.id] && <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {canCreate && clientId && (
                            <>
                                <button onClick={() => { setSelectedAsset(null); setAssetModalMode('add'); setAssetModalOpen(true); }} className="flex items-center gap-1.5 bg-[#422568] text-white px-4 py-1.5 rounded-lg text-[11px] font-black hover:bg-[#422568] transition-all shadow-lg shadow-[#422568]/20 active:scale-95 uppercase tracking-wider">
                                    <Plus className="w-4 h-4" /> Add Asset
                                </button>
                                <button onClick={() => setImportModal(true)} className="flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 px-4 py-1.5 rounded-lg text-[11px] font-black hover:bg-slate-50 transition-all shadow-sm active:scale-95 uppercase tracking-wider">
                                    <Import className="w-4 h-4 text-[#422568]" /> Import
                                </button>
                            </>
                        )}
                        {/* <button onClick={() => setShowExportModal(true)} className="flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 px-4 py-1.5 rounded-lg text-[11px] font-black hover:bg-slate-50 transition-all shadow-sm active:scale-95 uppercase tracking-wider">
                            <Download className="w-4 h-4 text-emerald-600" /> Export
                        </button> */}
                        <LocationSelectorSection
                            ids={locationIds}
                            onIdsChange={handleLocationChange}
                        />
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-20 animate-in fade-in duration-500">
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/80 border-b border-slate-100 backdrop-blur-md">
                                    {visibleColumns.asset_name && (
                                        <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-[#422568] transition-all" onClick={() => handleSort('name')}>
                                            <div className="flex items-center gap-2">Asset Name <SortIcon field="name" /></div>
                                        </th>
                                    )}
                                    {visibleColumns.hostname && (
                                        <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-[#422568] transition-all" onClick={() => handleSort('hostname')}>
                                            <div className="flex items-center gap-2">Hostname <SortIcon field="hostname" /></div>
                                        </th>
                                    )}
                                    {visibleColumns.ip_address && (
                                        <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer hover:text-[#422568] transition-all" onClick={() => handleSort('ip_address')}>
                                            <div className="flex items-center gap-2">IP Address <SortIcon field="ip_address" /></div>
                                        </th>
                                    )}
                                    {visibleColumns.mac && <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">MAC Address</th>}
                                    {visibleColumns.os && <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">OS</th>}
                                    {visibleColumns.manufacturer && <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">Manufacturer</th>}
                                    {visibleColumns.type && <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">Type</th>}
                                    {visibleColumns.status && <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Status</th>}
                                    {visibleColumns.tags && <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">Tags</th>}
                                    {visibleColumns.last_seen && <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">Last Seen</th>}
                                    {/* {visibleColumns.protocols && <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">Protocols</th>} */}
                                    {visibleColumns.last_frame && (
                                        <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <Eye className="w-3.5 h-3.5 text-[#422568]" />
                                                <span>Last Frame</span>
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.live_stream && (
                                        <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <Play className="w-3.5 h-3.5 text-[#422568]" />
                                                <span>Live Stream</span>
                                            </div>
                                        </th>
                                    )}
                                    <th className="px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan={15} className="py-24 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <Loader2 className="w-8 h-8 text-[#422568] animate-spin" />
                                                <p className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Retrieving Infrastructure Data</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : assetsData.length === 0 ? (
                                    <tr>
                                        <td colSpan={15} className="py-24 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <Box className="w-10 h-10 text-slate-200" />
                                                <p className="text-[11px] font-black text-slate-300 uppercase tracking-widest mt-2">No Assets Discovered</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    assetsData.map((item) => {
                                        const config = getAssetIconConfig(item.asset_type);
                                        const { icon } = getOSIcon(item.os_name);
                                        const Icon = config.icon;
                                        return (
                                            <tr key={item.id} className="hover:bg-slate-50/80 transition-all group">
                                                {visibleColumns.asset_name && (
                                                    <td className="px-3 py-1.5">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`p-1.5 rounded-lg bg-white border border-slate-100 shadow-sm ${config.color} group-hover:scale-110 transition-transform`}>
                                                                <Icon size={14} />
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span
                                                                    className="font-bold text-slate-900 text-[11px] hover:text-[#422568] transition-colors uppercase tracking-tight cursor-pointer"
                                                                    onClick={() => handleAssetClick(item.has_oob, item.oob_type || "", item.is_ad_machine || false, item.name, item.id, item.client_id, item.agent_id, item.organization_id, item.asset_type, item.hostname)}
                                                                >
                                                                    {item.name || 'Unnamed'}
                                                                </span>
                                                                {item.is_primary && <span className="text-[8px] font-black text-[#422568] bg-[#422568] px-1 py-0.5 rounded uppercase border border-[#422568] shadow-sm">CORE</span>}
                                                                <button onClick={(e) => handleInfoClick(e, item)} className="text-slate-800 hover:text-[#422568] transition-colors p-0.5" data-info-popover>
                                                                    <Info size={11} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.hostname && (
                                                    <td className="px-3 py-1.5 font-mono">
                                                        <div className="flex items-center gap-2 text-[10px] text-slate-600 font-bold group/copy cursor-pointer" onClick={() => copyToClipboard(item.hostname, `host-${item.id}`)}>
                                                            <Globe size={11} className="text-slate-800" />
                                                            {item.hostname || '--'}
                                                            {item.hostname && (copiedField === `host-${item.id}` ? <Check className="w-2.5 h-2.5 text-emerald-500" /> : <Copy className="w-2.5 h-2.5 " />)}
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.ip_address && (
                                                    <td className="px-3 py-1.5 font-mono">
                                                        <div className="flex items-center gap-2 text-[11px] text-slate-900 font-black group/copy cursor-pointer" onClick={() => copyToClipboard(item.ip_address, `ip-${item.id}`)}>
                                                            <Network size={11} className="text-[#422568]" />
                                                            {item.ip_address || '--'}
                                                            {item.ip_address && (copiedField === `ip-${item.id}` ? <Check className="w-2.5 h-2.5 text-emerald-500" /> : <Copy className="w-2.5 h-2.5 " />)}
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.mac && <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500 font-bold">{item.mac_address || '--'}</td>}
                                                {visibleColumns.os && (
                                                    <td className="px-3 py-1.5">
                                                        <div className="flex items-center gap-2">
                                                            <img src={icon} alt="os" className="w-6 h-6" />
                                                            <span className="text-[10px] text-slate-700 font-bold leading-none">{item.os_name || "Generic OS"}</span>
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.manufacturer && <td className="px-3 py-1.5 text-[10px] text-slate-600 font-bold">{item.manufacturer || '--'}</td>}
                                                {visibleColumns.type && (
                                                    <td className="px-3 py-1.5">
                                                        <span className="text-[9px] font-black text-slate-400 border border-slate-100 bg-slate-50/50 px-1.5 py-0.5 rounded uppercase tracking-wider group-hover:border-slate-200 transition-colors">
                                                            {item.asset_type === 'local_asset' ? 'EndPoint Server' : item.asset_type?.replace(/_/g, ' ')}
                                                        </span>
                                                    </td>
                                                )}
                                                {visibleColumns.status && (
                                                    <td className="px-3 py-1.5 text-center">
                                                        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${item.status?.toLowerCase() === 'active' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'} shadow-sm`}>
                                                            <div className={`w-1 h-1 rounded-full ${getStatusBg(item.status)} ${item.status?.toLowerCase() === 'offline' ? 'animate-pulse' : ''}`} />
                                                            <span className="text-[9px] font-black uppercase tracking-wider">{item.status || 'Unknown'}</span>
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.tags && (
                                                    <td className="px-3 py-1.5">
                                                        <div className="flex flex-wrap gap-1 max-w-[150px]">
                                                            {(() => {
                                                                try {
                                                                    if (!item.resolved_tags) return <span className="text-slate-300 text-[10px]">--</span>;
                                                                    const tags = typeof item.resolved_tags === 'string' ? JSON.parse(item.resolved_tags) : item.resolved_tags;
                                                                    if (!Array.isArray(tags) || tags.length === 0) return <span className="text-slate-300 text-[10px]">--</span>;
                                                                    return tags.map((t: any) => (
                                                                        <span key={t.id} className="text-[8px] font-black bg-white border border-slate-200 text-slate-500 px-1.5 py-0.5 rounded uppercase transition-colors hover:border-[#422568] hover:text-[#422568]">
                                                                            {t.name}
                                                                        </span>
                                                                    ));
                                                                } catch { return null; }
                                                            })()}
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.last_seen && (
                                                    <td className="px-3 py-1.5">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-slate-900 font-bold">{formatDate(item.last_seen).split(',')[0]}</span>
                                                            <span className="text-[9px] text-slate-400 font-medium">{formatDate(item.last_seen).split(',')[1]}</span>
                                                        </div>
                                                    </td>
                                                )}
                                                {/* {visibleColumns.protocols && (
                                                    <td className="px-3 py-1.5">
                                                        <div className="flex flex-wrap gap-1 max-w-[120px]">
                                                            {(() => {
                                                                try {
                                                                    const p = typeof item.protocols_performed === 'string' ? JSON.parse(item.protocols_performed) : item.protocols_performed;
                                                                    if (!Array.isArray(p) || p.length === 0) return <span className="text-slate-300 text-[10px]">--</span>;
                                                                    return p.map((proto: string, idx: number) => (
                                                                        <span key={idx} className="text-[8px] font-black text-blue-600 bg-blue-50 px-1 py-0.5 rounded uppercase border border-blue-100">{proto}</span>
                                                                    ));
                                                                } catch { return <span className="text-slate-300 text-[10px]">--</span>; }
                                                            })()}
                                                        </div>
                                                    </td>
                                                )} */}
                                                {visibleColumns.last_frame && (
                                                    <td className="px-3 py-1.5 text-center">
                                                        {item.asset_type === 'camera' ? (
                                                            <button onClick={() => fetchLastFrame(item.client_id, item.id, item.name)} className="p-1.5 text-slate-400 hover:text-white hover:bg-[#422568] rounded-lg transition-all" title="View Last Frame">
                                                                <Eye className="w-4 h-4 " />
                                                            </button>
                                                        ) : (
                                                            <span className="text-slate-200">-</span>
                                                        )}
                                                    </td>
                                                )}
                                                {visibleColumns.live_stream && (
                                                    <td className="px-3 py-1.5 text-center">
                                                        {item.asset_type === 'camera' ? (
                                                            <button
                                                                onClick={() => handleOpenLiveStream(item)}
                                                                disabled={!(item.status?.toLowerCase() === 'active' || item.status?.toLowerCase() === 'online')}
                                                                className={`p-1.5 rounded-lg transition-all ${(item.status?.toLowerCase() === 'active' || item.status?.toLowerCase() === 'online')
                                                                    ? 'text-emerald-500 hover:text-white hover:bg-emerald-600'
                                                                    : 'text-slate-300 cursor-not-allowed'
                                                                    }`}
                                                                title="View Live Stream"
                                                            >
                                                                <Play className="w-4 h-4" />
                                                            </button>
                                                        ) : (
                                                            <span className="text-slate-200">-</span>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="px-3 py-1.5 text-right">
                                                    <div className="flex items-center gap-1 justify-end">
                                                        <button onClick={(e) => handleMenuOpen(e, item.id)} className="p-1.5 text-slate-400 hover:text-white hover:bg-[#422568] rounded-lg transition-all" data-menu-id={item.id}>
                                                            <CircleEllipsis className="w-4.5 h-4.5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="bg-white border-t border-slate-100 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <ItemsPerPageDropdown value={itemsPerPage} onChange={handleItemsPerPageChange} />
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                SHOWING <span className="text-slate-900">{startIndex + 1}-{Math.min(endIndex, totalItems)}</span> OF <span className="text-slate-900">{totalItems}</span>
                            </p>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-slate-400 hover:text-[#422568] disabled:opacity-30 transition-colors"><ChevronsLeft className="w-4 h-4" /></button>
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-slate-400 hover:text-[#422568] disabled:opacity-30 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                            <div className="px-3 py-1 bg-[#422568] text-white text-[11px] font-black rounded-lg shadow-lg shadow-[#422568]/20">{currentPage}</div>
                            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 text-slate-400 hover:text-[#422568] disabled:opacity-30 transition-colors"><ChevronRight className="w-4 h-4" /></button>
                            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-1.5 text-slate-400 hover:text-[#422568] disabled:opacity-30 transition-colors"><ChevronsRight className="w-4 h-4" /></button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals & Dropdowns */}
            {openMenuId && createPortal(
                <div className="fixed w-44 bg-white border border-slate-200 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] z-[9999] overflow-hidden p-1 animate-in zoom-in-95 duration-150" style={{ top: dropdownPosition.top, left: dropdownPosition.left }}>
                    <button onClick={() => handleEdit(assetsData.find(a => String(a.id) === openMenuId)!)} className="w-full text-left px-3 py-2 text-[11px] font-bold text-slate-700 hover:bg-slate-50 hover:text-[#422568] flex items-center gap-2.5 rounded-lg transition-all">
                        <Pencil className="w-3.5 h-3.5" /> Edit Asset
                    </button>
                    <button onClick={() => handleCredentials(assetsData.find(a => String(a.id) === openMenuId)!)} className="w-full text-left px-3 py-2 text-[11px] font-bold text-slate-700 hover:bg-slate-50 hover:text-[#422568] flex items-center gap-2.5 rounded-lg transition-all border-t border-slate-50 mt-1 pt-2">
                        <Key className="w-3.5 h-3.5 text-[#422568]" /> Credentials
                    </button>
                    <button onClick={() => handleAddTags(assetsData.find(a => String(a.id) === openMenuId)!)} className="w-full text-left px-3 py-2 text-[11px] font-bold text-slate-700 hover:bg-slate-50 hover:text-[#422568] flex items-center gap-2.5 rounded-lg transition-all border-t border-slate-50 mt-1 pt-2">
                        <Tag className="w-3.5 h-3.5 text-blue-500" /> Manage Tags
                    </button>
                    <button onClick={() => handleDeleteClick(assetsData.find(a => String(a.id) === openMenuId)!)} className="w-full text-left px-3 py-2 text-[11px] font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-2.5 rounded-lg transition-all border-t border-slate-50 mt-1 pt-2">
                        <Trash2 className="w-3.5 h-3.5" /> Delete Asset
                    </button>
                </div>,
                document.body
            )}

            {infoPopoverAsset && createPortal(
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[9999] flex items-center justify-center p-4" onClick={() => setInfoPopoverAsset(null)}>
                    <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden p-0 animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-slate-50 px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-[#422568] rounded-lg"><Info className="w-4 h-4 text-[#422568]" /></div>
                                <div>
                                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">{infoPopoverAsset.name}</h3>
                                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter mt-1">Asset Metadata</p>
                                </div>
                            </div>
                            <button onClick={() => setInfoPopoverAsset(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-5 max-h-[400px] overflow-y-auto custom-scroll space-y-4">
                            <div className="space-y-3">
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Calendar className="w-3 h-3" /> Lifecycle Timeline</h4>
                                <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-bold text-slate-400 uppercase">Purchase</span>
                                        <span className="text-[11px] font-black text-slate-900">{infoPopoverAsset.purchase_date || '--'}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-bold text-slate-400 uppercase">Installation</span>
                                        <span className="text-[11px] font-black text-slate-900">{infoPopoverAsset.installation_date || '--'}</span>
                                    </div>
                                    <div className="flex flex-col border-t border-slate-200 pt-2">
                                        <span className="text-[9px] font-bold text-rose-500 uppercase">Warranty Exp</span>
                                        <span className="text-[11px] font-black text-rose-600">{infoPopoverAsset.warranty_expiration || '--'}</span>
                                    </div>
                                    <div className="flex flex-col border-t border-slate-200 pt-2">
                                        <span className="text-[9px] font-bold text-rose-500 uppercase">AMC Exp</span>
                                        <span className="text-[11px] font-black text-rose-600">{infoPopoverAsset.amc_expiration || '--'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <AddAssetModal open={assetModalOpen} mode={assetModalMode} asset={selectedAsset} onClose={() => setAssetModalOpen(false)} onSuccess={handleRefresh} />
            <AddCredentialModal open={credentialModalOpen} assetId={credentialAssetId} clientId={credentialClientId} onClose={() => setCredentialModalOpen(false)} isCamera={isCamera} assetType={credentialAssetType} initialAgentId={initialAgentId} />
            <AddTagModal open={tagModalOpen} assetId={tagAssetId} assetName={tagAssetName} onClose={() => setTagModalOpen(false)} onSuccess={handleRefresh} />
            <DeleteConfirmationModal open={deleteModalOpen} isDeleting={isDeleting} assetName={assetToDelete?.name} onClose={() => { setDeleteModalOpen(false); setAssetToDelete(null); }} onConfirm={confirmDelete} />
            <UploadModal open={importModal} onClose={() => setImportModal(false)} onSuccess={handleRefresh} />
            <AgentExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} templateName="assets" clientId={level?.id} />

            {/* Last Frame Modal */}
            {lastFrameModalOpen && createPortal(
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-emerald-50 rounded-xl shadow-sm border border-emerald-100"><Eye className="w-5 h-5 text-emerald-600" /></div>
                                <div>
                                    <h3 className="text-xs font-black uppercase tracking-[0.1em] text-slate-900">{lastFrameAssetName}</h3>
                                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5 flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                        Visual Identity Feed
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => setLastFrameModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-all group">
                                <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                            </button>
                        </div>
                        <div className="p-4 bg-slate-100/30">
                            {lastFrameLoading ? (
                                <div className="h-[50vh] flex flex-col items-center justify-center text-slate-400 gap-4 bg-white rounded-2xl border border-dashed border-slate-200 shadow-inner">
                                    <Loader2 className="w-10 h-10 animate-spin text-emerald-500" />
                                    <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Synchronizing Visual Data...</span>
                                </div>
                            ) : lastFrameImage ? (
                                <div className="space-y-4">
                                    <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-black shadow-2xl ring-1 ring-white/10 group">
                                        <img
                                            src={`data:image/jpeg;base64,${lastFrameImage}`}
                                            alt="last frame"
                                            className="w-full h-auto object-contain max-h-[70vh] mx-auto transition-transform duration-500 group-hover:scale-[1.01]"
                                        />
                                        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                                    </div>
                                </div>
                            ) : (
                                <div className="h-[50vh] flex flex-col items-center justify-center text-slate-400 gap-4 bg-white rounded-2xl border border-dashed border-slate-200 shadow-inner">
                                    <div className="p-4 bg-rose-50 rounded-full"><AlertTriangle className="w-10 h-10 text-rose-500" /></div>
                                    <div className="text-center">
                                        <span className="text-[12px] font-black uppercase tracking-[0.2em] text-slate-900 block mb-1">Visual Stream Unavailable</span>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Verify camera credentials and connectivity</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <LiveStreamModal
                open={liveStreamModalOpen}
                onClose={() => { setLiveStreamModalOpen(false); setLiveStreamCamera(null); }}
                camera={liveStreamCamera}
            />
        </div>
    );
};

export default AssetsTable;
